import React, {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  type SyntheticEvent,
  memo,
} from 'react'
import { TextSkeleton } from '@app/components/placeholders'
import { classNames } from '@app/utils/classes'
import { Button } from '../buttons'
import { twMerge } from 'tailwind-merge'

type ProfileInputProps = {
  disabledWall?: boolean
  skeletonLoad?: boolean
  redBorder?: boolean
  title: string
  subTitle?: string
  titleClassName?: string // title class name
  className?: string // subtitle class name
  subChildren?: any
  inputProps?: {
    inputType?: HTMLInputElement['type']
    onChange?(e: any): void
    value?: string
    disabled?: boolean
    defaultValue?: string
    placeholder?: string
    minLength?: number
    pattern?: string
  }
  onSubmit?(e?: any): void
  onCancel?(x?: any): void
  submitLabel?: {
    title?: ReactNode | string
    cancel?: string
    submit?: ReactNode | string
    disabled?: boolean
    cancelDisabled?: boolean
    submitClassName?: string
    left?: boolean
  }
  headingID?: string
}

const inputBorderColor = `border-black dark:border-gray-700`
const containerStyle = `rounded border-2 rounded-sm ${inputBorderColor} box-border`

// input container style for profile and settings
const ProfileInputCellComponent: FC<PropsWithChildren<ProfileInputProps>> = ({
  skeletonLoad,
  title,
  subTitle,
  className,
  titleClassName = '',
  inputProps,
  onSubmit,
  submitLabel,
  children,
  redBorder,
  disabledWall,
  onCancel,
  headingID,
}) => {
  const {
    inputType,
    onChange,
    value,
    disabled,
    defaultValue,
    placeholder,
    minLength,
    pattern,
  } = inputProps ?? {}

  const onCancelEvent = (e: SyntheticEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    if (onCancel) {
      onCancel(e)
    } else {
      onChange && onChange({ target: { value: defaultValue } })
    }
  }

  const BaseComponent = onSubmit ? 'form' : 'div'

  const submitBtn = submitLabel?.submit
  const submitTitle = submitLabel?.title
  const submitBtnDisabled = submitLabel?.disabled
  const submitClassName = submitLabel?.submitClassName
  const left = submitLabel?.left
  const cancelDisabled = submitLabel?.cancelDisabled

  // disable the submit
  const smtDisabled = disabled || submitBtnDisabled

  return (
    <BaseComponent
      className={classNames(
        containerStyle,
        redBorder ? 'border-red-700 dark:border-red-700' : '',
        disabledWall ? 'bg-gray-100 dark:bg-gray-800' : ''
      )}
      onSubmit={onSubmit}
    >
      <div
        className={`p-6 ${
          submitLabel ? 'border-b-2' : ''
        } ${inputBorderColor} relative`}
      >
        <>
          {disabledWall ? (
            <div className='absolute top-0 right-0 left-0 bottom-0 z-1 cursor-not-allowed'></div>
          ) : null}
          <h3
            className={classNames(`text-base font-semibold`, titleClassName)}
            id={headingID}
          >
            {title}
          </h3>
          {subTitle ? (
            <div className='py-2'>
              <p className='text-sm text-gray-800 dark:text-gray-200'>
                {subTitle}
              </p>
            </div>
          ) : null}
          {inputProps ? (
            <div className='py-2'>
              {skeletonLoad ? (
                <TextSkeleton className={className} size={'base'} />
              ) : (
                <input
                  className={
                    'border rounded px-2.5 py-1.5 w-72 dark:border-gray-600'
                  }
                  type={inputType ?? 'email'}
                  value={value}
                  onChange={onChange}
                  placeholder={placeholder}
                  minLength={minLength}
                  pattern={pattern}
                />
              )}
            </div>
          ) : null}
          {children}
        </>
      </div>
      {submitLabel ? (
        <div className='flex place-items-center place-content-between py-1.5 px-3 bg-gray-100 dark:bg-gray-800 min-h-[46px]'>
          {submitTitle ? (
            <div className='dark:text-gray-100 text-sm'>{submitTitle}</div>
          ) : left ? null : (
            <span />
          )}
          <div className='flex place-items-center space-x-3'>
            {!disabled && submitLabel?.cancel && !cancelDisabled ? (
              <Button
                type='button'
                border={false}
                onClick={onCancelEvent}
                className={classNames(
                  disabled
                    ? ''
                    : 'hover:text-gray-900 hover:border-gray-600 dark:hover:text-gray-300',
                  'text-gray-700 dark:text-gray-100'
                )}
              >
                {submitLabel?.cancel}
              </Button>
            ) : null}
            {submitBtn && typeof submitBtn === 'string' ? (
              <Button
                type='submit'
                border={false}
                round={false}
                disabled={submitBtnDisabled}
                className={twMerge(
                  'rounded border min-w-[8rem] md:min-w-[10rem] text-sm',
                  !redBorder ? 'border-black dark:border-white' : '',
                  smtDisabled
                    ? ''
                    : redBorder
                      ? 'hover:text-gray-100 hover:bg-red-600'
                      : '',
                  redBorder
                    ? 'text-white bg-red-800 border-red-800 dark:border-red-800'
                    : submitClassName ??
                        `bg-black text-gray-100 dark:bg-white dark:text-gray-800 dark:border-gray-600${
                          smtDisabled
                            ? ''
                            : ' hover:bg-gray-700 dark:hover:bg-gray-300'
                        }`
                )}
              >
                {submitBtn}
              </Button>
            ) : (
              submitBtn ?? null
            )}
          </div>
        </div>
      ) : null}
    </BaseComponent>
  )
}

export const ProfileInputCell = memo(ProfileInputCellComponent)
export const profileSubmitClassHover = `hover:text-blue-500 hover:border-blue-500 dark:hover:border-blue-400 dark:hover:text-blue-500`
export const profileCellSubmitStyle = twMerge(
  'hover:text-gray-600 dark:hover:text-gray-400 border-black dark:border-white',
  'rounded border min-w-[8rem] md:min-w-[10rem] text-sm',
  'px-2 py-1 md:px-5 rendering-legibility',
  profileSubmitClassHover
)
